import { useState, useContext, useEffect } from 'react';

import { UserDataContext, ErrorMessageContext } from '../contexts';
import { getAuthSession, isSessionExpiredError } from '../utils/authSession';

import useAsync from './useAsync';
import useSignOut from './useSignOut';

export default function useFetch(givenOptions = {}) {
  const [options, setOptions] = useState(givenOptions);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [status, setStatus] = useState(null);

  const { userData } = useContext(UserDataContext);
  const { showErrorMessage, setShowErrorMessage } = useContext(ErrorMessageContext);

  const [, currentUserSessionRequest] = useAsync();
  const [, fetchRequest] = useAsync();
  const [, parseJsonRequest] = useAsync();
  const [signOut] = useSignOut();

  function buildFetchOptions() {
    if (options.s3Call) return { fetchUrl: options.url, body: null };

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    let fetchUrl = `${backendUrl}${options.url}`;

    if (options.urlIds) {
      options.urlIds.forEach((id, indexId) => {
        const urlId = userData.userAttributes?.[id] || userData.accountData?.[id] || userData.companyData?.[id] || userData.userTransactions?.[0][id] || id;
        fetchUrl += indexId !== 0 ? `&${urlId}` : `/${urlId}`;
      });
    }

    const body = options.body && Object.keys(options.body).length ? options.body : null;

    if (body && options.bodyIds) {
      options.bodyIds.forEach((id) => {
        body[id] = userData.userAttributes?.[id] || userData.accountData?.[id] || userData.accountData?.[id] || userData.userTransactions?.[0][id] || id;
      });
    }

    return { fetchUrl, body };
  }

  function makeCall() {
    setCallEnded(false);
    setSuccess(false);
    setError(false);
    setResponse(null);

    setLoading(true);

    const { fetchUrl, body } = buildFetchOptions();

    const authSessionPromise = !options.s3Call ? getAuthSession : () => Promise.resolve(null);

    currentUserSessionRequest({
      promise: authSessionPromise,
      onSuccess: (session) => {
        let fetchCall = () => fetch(fetchUrl);
        if (!options.s3Call) {
          const JWT = session.tokens.idToken.toString();
          const fetchOptions = ({
            method: options.method || !!options.body ? 'post' : 'get',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${JWT}`,
            },
            ...body && { body: JSON.stringify(body) },
          });
          fetchCall = () => fetch(fetchUrl, fetchOptions);
        }
        fetchRequest({
          promise: fetchCall,
          onSuccess: (fetchData) => {
            setStatus(fetchData.status);
            if (fetchData.status >= 400) {
              setError(true);
              if (options.onError) options.onError();
              const errorMessage = `error with call: ${options.url}, error code: ${fetchData.status}`;
              setShowErrorMessage(!showErrorMessage.includes(errorMessage) ? `${showErrorMessage}\n${errorMessage}` : showErrorMessage);
            } else if (fetchData.status === 204) {
              setSuccess(true);
              if (options.onSuccess) options.onSuccess(null, 204);
            } else {
              parseJsonRequest({
                promise: () => fetchData.json(),
                onSuccess: (parsedJsonData) => {
                  setResponse(parsedJsonData);
                  if (!options.multipleCalls || options.multipleCalls === 1) {
                    setSuccess(true);
                    if (options.onSuccess) options.onSuccess(parsedJsonData, fetchData.status);
                  }
                },
              });
            }
          },
          onError: (e) => {
            setError(true);
            if (options.onError) options.onError();
            else setShowErrorMessage(!showErrorMessage.includes(e.toString()) ? `${showErrorMessage}\n${e.toString()}` : showErrorMessage);
          },
        });
      },
      onError: (e) => {
        setError(true);
        setCallEnded(true);
        setLoading(false);
        if (options.onError) options.onError();
        if (isSessionExpiredError(e)) {
          signOut({ inactivity: true });
        }
      },
    });
  }

  useEffect(() => { if (options.url && !loading) makeCall(); }, [options]);

  useEffect(() => { if (error || success) setCallEnded(true); }, [error, success]);

  useEffect(() => {
    if (callEnded) {
      if (options.onFinally) options.onFinally();
      setOptions({});
      setLoading(false);
    }
  }, [callEnded]);

  return [{ response, loading, success, callEnded, status }, setOptions];
}
